/** @format */

import { getQuestionContentText, ParentGroupSummary, QuestionSpecification } from "@question-bank/shared";
import { MathText } from "./MathText";
import { toStringArray } from "../utils/helpers";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useParams } from "react-router-dom";
import { trackEvent } from "../utils/analytics";
import { useState } from "react";

interface QuestionCardProps {
	question: ParentGroupSummary;
	onClick?: () => void;
	onAdd?: (question: ParentGroupSummary) => void;
	onRemove?: (question: ParentGroupSummary) => void;
	isAdded?: boolean;
	/** `true` when the question's paper carries additional resources (attachments). */
	hasResources?: boolean;
}

const pillStyle =
	"font-bold not-italic text-[13px] inline-flex items-center py-0.5 px-2.5 rounded-[5px] gap-2.5 font-lato leading-normal text-center";
/** Format the parent's main specification as a breadcrumb (theme › topic › subTopic). */
const formatSpecification = (specification?: QuestionSpecification): string =>
	specification ? [specification.theme, specification.topic, specification.subTopic].filter(Boolean).join(" › ") : "";

export const QuestionCard = ({ question, onClick, onAdd, onRemove, isAdded, hasResources }: QuestionCardProps) => {
	const { t, i18n } = useTranslation();
	const { locale } = useParams();
	const location = useLocation();

	const subjectLabel = toStringArray(question.subject).join(", ") || "Unknown";
	const title = `${subjectLabel} (Q: ${question.questionNumber || "?"})  `;
	const specificationLabel = formatSpecification(question.specifications?.[0]);
	const minutes = question.totalMarks; // mins = marks currently
	const isWelsh = i18n.language?.toLowerCase().startsWith("cy");
	const questionContent = isWelsh
		? (getQuestionContentText(question, "cy") ?? getQuestionContentText(question, "en"))
		: (getQuestionContentText(question, "en") ?? getQuestionContentText(question, "cy"));

	const cartActionLabel = isAdded ? t("questionCard.removeFromPaper") : t("questionCard.addToPaper");

	const [hasJustAdded, setHasJustAdded] = useState(false);
	const shouldShowRemoveIntent = Boolean(isAdded) && !hasJustAdded;
	const removeIntentClassName = "hover:bg-(--public-color-btn-remove) focus-visible:bg-(--public-color-btn-remove)";
	const addedStateClassName = shouldShowRemoveIntent
		? `bg-(--public-color-btn-success) ${removeIntentClassName}`
		: "bg-(--public-color-btn-success)";
	const cartActionClassName = `group relative flex items-center justify-center h-9.5 w-9.5 rounded-md text-white ${
		isAdded ? addedStateClassName : "bg-(--public-color-btn-add)"
	} cursor-pointer overflow-hidden transition-colors duration-200 motion-reduce:transition-none`;

	const cartGlyphClassName = "absolute inset-0 m-auto size-6";
	const glyphMotionClassName = "transition-transform duration-200 ease-out motion-reduce:transition-none";
	const addedGlyphHoverExit = "group-hover:-translate-y-[150%] group-focus-visible:-translate-y-[150%]";
	const addGlyphPosition = !isAdded
		? `translate-y-0 ${glyphMotionClassName}`
		: shouldShowRemoveIntent
			? "translate-y-[150%]"
			: `-translate-y-[150%] ${glyphMotionClassName}`;
	const addedGlyphPosition = !isAdded
		? "translate-y-[150%]"
		: `translate-y-0 ${shouldShowRemoveIntent ? addedGlyphHoverExit : ""} ${glyphMotionClassName}`;
	const removeGlyphPosition = !isAdded
		? `-translate-y-[150%] ${glyphMotionClassName}`
		: shouldShowRemoveIntent
			? `translate-y-[150%] group-hover:translate-y-0 group-focus-visible:translate-y-0 ${glyphMotionClassName}`
			: "translate-y-[150%]";

	return (
		<div
			className="flex flex-col h-58.25 w-full rounded-md border-2 border-(--public-color-border-default)"
			data-testid="question-card"
			data-question-number={question.rootQuestionNumber}
		>
			<Link
				onClick={() => {
					trackEvent("view_question", { question_id: question.parentQuestionId, subject: subjectLabel });
					onClick?.();
				}}
				to={`/${locale ?? "en"}/questions/${question.parentQuestionId}`}
				state={{ backgroundLocation: location }}
				tabIndex={0}
				className="flex flex-col h-full w-full text-left bg-white cursor-pointer rounded-md"
			>
				<div className="flex flex-row h-auto min-h-13.75 border-b border-(--public-color-question-card-header-border) py-2 px-2 items-start justify-between bg-(--public-color-question-card-header-bg)">
					<div className="flex flex-col gap-0.5 min-w-0">
						<span className=" flex items-center gap-1 capitalize text-[20px] text-(--public-color-text-filter) font-roboto">
							{title}
							{hasResources && (
								<span
									role="img"
									aria-label={t("questionCard.additionalResources")}
									title={t("questionCard.additionalResources")}
									className="flex shrink-0 text-(--public-color-theme-primary)"
								>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										fill="none"
										viewBox="0 0 24 24"
										strokeWidth={1.5}
										stroke="currentColor"
										className="size-3.5"
										aria-hidden="true"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13"
										/>
									</svg>
								</span>
							)}
						</span>
						{specificationLabel && (
							<span className="text-[13px] font-lato text-gray-500 truncate" title={specificationLabel}>
								{specificationLabel}
							</span>
						)}
					</div>
					<div className="flex gap-1.25 justify-end shrink-0">
						{(question.qualification.title ?? question.qualification.code) && (
							<span className={`${pillStyle} bg-(--public-gcse-theme)`}>
								{question.qualification.title ?? question.qualification.code}
							</span>
						)}
						{question.tier && (
							<span className={`${pillStyle} bg-(--public-difficulty-pill)`}>
								{question.tier.title ?? question.tier.code}
							</span>
						)}
					</div>
				</div>

				<div className="flex-1 px-2 py-2 overflow-hidden">
					{questionContent && (
						<p
							className="text-[16px] font-roboto text-gray-700 line-clamp-4 whitespace-pre-wrap"
							data-testid="question-content"
						>
							<MathText text={questionContent} />
						</p>
					)}
				</div>

				<div className=" flex flex-row mt-auto p-2 items-center border-t border-(--public-color-question-card-header-border)">
					<div className="flex items-center gap-4 text-gray-600 text-[14px] whitespace-nowrap min-w-0 overflow-hidden">
						{
							<span className="flex items-center gap-2">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
									strokeWidth={1.5}
									stroke="currentColor"
									className="size-4"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
									/>
								</svg>
								{minutes} {t("questionCard.minutes")}
							</span>
						}
						{
							<span className="flex items-center gap-2">
								<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
									<g clipPath="url(#clip0_3156_5464)">
										<path
											d="M14 1C14.2652 1 14.5196 1.10536 14.7071 1.29289C14.8946 1.48043 15 1.73478 15 2V14C15 14.2652 14.8946 14.5196 14.7071 14.7071C14.5196 14.8946 14.2652 15 14 15H2C1.73478 15 1.48043 14.8946 1.29289 14.7071C1.10536 14.5196 1 14.2652 1 14V2C1 1.73478 1.10536 1.48043 1.29289 1.29289C1.48043 1.10536 1.73478 1 2 1H14ZM2 0C1.46957 0 0.960859 0.210714 0.585786 0.585786C0.210714 0.960859 0 1.46957 0 2L0 14C0 14.5304 0.210714 15.0391 0.585786 15.4142C0.960859 15.7893 1.46957 16 2 16H14C14.5304 16 15.0391 15.7893 15.4142 15.4142C15.7893 15.0391 16 14.5304 16 14V2C16 1.46957 15.7893 0.960859 15.4142 0.585786C15.0391 0.210714 14.5304 0 14 0L2 0Z"
											fill="#767676"
										/>
										<path
											d="M10.9702 4.97028C11.1101 4.83155 11.299 4.75348 11.4961 4.75293C11.6931 4.75239 11.8825 4.82942 12.0232 4.96737C12.1639 5.10533 12.2447 5.29312 12.248 5.49015C12.2514 5.68718 12.1771 5.87761 12.0412 6.02028L8.04916 11.0103C7.98055 11.0842 7.89774 11.1435 7.80569 11.1846C7.71364 11.2258 7.61423 11.248 7.51341 11.2499C7.41259 11.2517 7.31243 11.2332 7.21891 11.1955C7.1254 11.1578 7.04046 11.1016 6.96916 11.0303L4.32416 8.38428C4.25047 8.31562 4.19137 8.23282 4.15038 8.14082C4.10938 8.04882 4.08734 7.94951 4.08557 7.8488C4.08379 7.7481 4.10231 7.64807 4.14004 7.55468C4.17776 7.46129 4.2339 7.37646 4.30512 7.30524C4.37634 7.23402 4.46117 7.17788 4.55456 7.14016C4.64795 7.10244 4.74798 7.08391 4.84868 7.08569C4.94938 7.08747 5.0487 7.10951 5.1407 7.1505C5.2327 7.19149 5.3155 7.25059 5.38416 7.32428L7.47816 9.41728L10.9512 4.99228L10.9702 4.97028Z"
											fill="#767676"
										/>
									</g>
									<defs>
										<clipPath id="clip0_3156_5464">
											<rect width="16" height="16" fill="white" />
										</clipPath>
									</defs>
								</svg>
								{question.totalMarks} {t("paperModal.marks")}
							</span>
						}
						{question.childCount > 1 && (
							<span className="flex items-center gap-2">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
									strokeWidth={1.5}
									stroke="currentColor"
									className="size-4"
								>
									<rect x="9" y="3" width="6" height="4" rx="1" />
									<rect x="3" y="17" width="6" height="4" rx="1" />
									<rect x="15" y="17" width="6" height="4" rx="1" />
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M12 7v4m-6 6v-2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v2"
									/>
								</svg>
								{question.childCount} {t("questionCard.parts")}
							</span>
						)}
					</div>
					<div className="ml-auto shrink-0 pl-8">
						<button
							data-testid="btn-add-question"
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								e.preventDefault();
								if (isAdded) {
									trackEvent("remove_from_paper", { question_id: question.parentQuestionId, subject: subjectLabel });
									onRemove?.(question);
								} else {
									trackEvent("add_to_paper", { question_id: question.parentQuestionId, subject: subjectLabel });
									setHasJustAdded(true);
									onAdd?.(question);
								}
							}}
							onMouseLeave={() => setHasJustAdded(false)}
							onBlur={() => setHasJustAdded(false)}
							aria-label={cartActionLabel}
							title={cartActionLabel}
							className={cartActionClassName}
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								viewBox="0 0 24 24"
								fill="none"
								strokeWidth={1.5}
								stroke="currentColor"
								className={`${cartGlyphClassName} ${addGlyphPosition}`}
								aria-hidden="true"
							>
								<path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
							</svg>

							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								stroke="currentColor"
								strokeWidth={1.5}
								strokeLinecap="round"
								strokeLinejoin="round"
								viewBox="0 0 24 24"
								className={`${cartGlyphClassName} ${addedGlyphPosition}`}
								aria-hidden="true"
							>
								<path d="m4.5 12.75 6 6 9-13.5" />
							</svg>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								strokeWidth={1.5}
								stroke="currentColor"
								className={`${cartGlyphClassName} ${removeGlyphPosition}`}
								aria-hidden="true"
							>
								<path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12h-15" />
							</svg>
						</button>
					</div>
				</div>
			</Link>
		</div>
	);
};

export const QuestionCardSkeleton = () => (
	<div className="flex flex-col h-58.25 w-full rounded-md animate-pulse border-2 border-(--public-color-border-default)">
		<div className="flex flex-row h-13.75 py-2 px-2 items-center justify-between border-b border-(--public-color-question-card-header-border) bg-(--public-color-question-card-header-bg)">
			<div className="h-5 w-48 bg-gray-200 rounded" />
			<div className="flex gap-1.25">
				<div className="h-5 w-13.25 bg-gray-200 rounded-[5px]" />
				<div className="h-5 w-23.75 bg-gray-200 rounded-[5px]" />
			</div>
		</div>

		<div className="h-28 p-2">
			<div className="h-4 w-48 bg-gray-200 rounded mt-2" />
		</div>

		<div className="flex flex-row mt-auto p-2 items-center">
			<div className="flex gap-5">
				<div className="h-4 w-16 bg-gray-200 rounded" />
				<div className="h-4 w-12 bg-gray-200 rounded" />
				<div className="h-4 w-14 bg-gray-200 rounded" />
			</div>
			<div className="ml-auto">
				<div className="h-9.5 w-9.5 bg-gray-200 rounded-md" />
			</div>
		</div>
	</div>
);
